import { 
  logEvent, 
  logAuthEvent, 
  logAdminEvent, 
  logUIEvent, 
  logContentEvent, 
  logChatEvent, 
  logProfileEvent, 
  logSystemEvent, 
  logErrorEvent, 
  logPerformanceEvent 
} from '../services/firebase';

/**
 * Comprehensive Activity Logger
 * Tracks every single activity in the application with detailed metadata
 */
class ActivityLogger {
  constructor() {
    this.sessionId = this.generateSessionId();
    this.pageLoadTime = performance.now();
    this.activityCount = 0;
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get caller information for better tracking
   */
  getCallerInfo() {
    const stack = new Error().stack;
    const lines = stack.split('\n');
    
    // Find the first line that contains a component file
    for (let i = 3; i < lines.length; i++) {
      const line = lines[i];
      if (line.includes('.js') || line.includes('.jsx')) {
        const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/);
        if (match) {
          const [, functionName, filePath, lineNumber] = match;
          const fileName = filePath.split('/').pop() || filePath.split('\\').pop();
          return { function: functionName, file: fileName, line: lineNumber };
        }
      }
    }
    
    return { function: 'unknown', file: 'unknown', line: 'unknown' };
  }

  /**
   * Enhanced logging with automatic caller detection
   */
  async log(type, action, details, metadata = {}) {
    try {
      this.activityCount++;
      const caller = this.getCallerInfo();
      
      const enhancedMetadata = {
        ...metadata,
        sessionId: this.sessionId,
        activityCount: this.activityCount,
        callerFile: caller.file,
        callerFunction: caller.function,
        callerLine: caller.line,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      };

      return await logEvent(type, action, details, enhancedMetadata);
    } catch (error) {
      console.error('Activity logging failed:', error);
      return null;
    }
  }

  // Authentication Activities
  async logLogin(email, success = true, errorMessage = null) {
    const action = success ? 'login_success' : 'login_failed';
    const details = success 
      ? `User successfully logged in: ${email}` 
      : `Login failed for ${email}: ${errorMessage}`;
    
    return await this.log('authentication', action, details, { email, success, errorMessage });
  }

  async logLogout(email) {
    return await this.log('authentication', 'logout', `User logged out: ${email}`, { email });
  }

  async logRegistration(email, success = true, errorMessage = null) {
    const action = success ? 'registration_success' : 'registration_failed';
    const details = success 
      ? `User registered successfully: ${email}` 
      : `Registration failed for ${email}: ${errorMessage}`;
    
    return await this.log('authentication', action, details, { email, success, errorMessage });
  }

  async logPasswordChange(email, success = true) {
    const action = success ? 'password_changed' : 'password_change_failed';
    const details = `Password change ${success ? 'successful' : 'failed'} for: ${email}`;
    
    return await this.log('authentication', action, details, { email, success });
  }

  // Admin Activities
  async logAdminLogin(email, role, success = true) {
    const action = success ? 'admin_login_success' : 'admin_login_failed';
    const details = `Admin ${success ? 'logged in' : 'login failed'}: ${email} (${role})`;
    
    return await this.log('admin_activity', action, details, { email, role, success });
  }

  async logAdminAction(action, details, targetId = null, changes = {}) {
    return await this.log('admin_activity', action, details, { targetId, changes });
  }

  async logAdminUserManagement(action, targetEmail, targetRole, changes = {}) {
    const details = `Admin user ${action}: ${targetEmail} (${targetRole})`;
    return await this.log('admin_management', action, details, { targetEmail, targetRole, changes });
  }

  // UI and Settings Changes
  async logColorChange(element, oldColor, newColor) {
    const details = `Color changed for ${element}: ${oldColor} → ${newColor}`;
    return await this.log('ui_changes', 'color_changed', details, { element, oldColor, newColor });
  }

  async logThemeChange(oldTheme, newTheme) {
    const details = `Theme changed: ${oldTheme} → ${newTheme}`;
    return await this.log('ui_changes', 'theme_changed', details, { oldTheme, newTheme });
  }

  async logSettingsUpdate(section, changes) {
    const details = `Settings updated in ${section}: ${Object.keys(changes).join(', ')}`;
    return await this.log('ui_changes', 'settings_updated', details, { section, changes });
  }

  async logNavigationChange(fromPage, toPage) {
    const details = `Navigation: ${fromPage} → ${toPage}`;
    return await this.log('ui_changes', 'navigation_changed', details, { fromPage, toPage });
  }

  // Chat Activities
  async logChatStart(userId = null, userEmail = null) {
    const details = `Chat session started by ${userEmail || 'anonymous user'}`;
    return await this.log('chat_activity', 'chat_started', details, { userId, userEmail });
  }

  async logMessage(sender, messageLength, messagePreview) {
    const details = `${sender} sent message (${messageLength} chars): ${messagePreview}`;
    return await this.log('chat_activity', 'message_sent', details, { sender, messageLength });
  }

  async logAIResponse(provider, responseLength, responseTime) {
    const details = `AI response generated via ${provider} (${responseLength} chars, ${responseTime}ms)`;
    return await this.log('chat_activity', 'ai_response_generated', details, { provider, responseLength, responseTime });
  }

  async logChatEnd(messageCount, duration) {
    const details = `Chat session ended: ${messageCount} messages, ${duration}ms duration`;
    return await this.log('chat_activity', 'chat_ended', details, { messageCount, duration });
  }

  // Profile Activities
  async logProfileUpdate(userId, changes) {
    const details = `Profile updated: ${Object.keys(changes).join(', ')}`;
    return await this.log('profile_management', 'profile_updated', details, { userId, changes });
  }

  async logProfileView(viewedUserId, viewerUserId = null) {
    const details = `Profile viewed: ${viewedUserId}`;
    return await this.log('profile_management', 'profile_viewed', details, { viewedUserId, viewerUserId });
  }

  // Content Management
  async logContentAdd(contentType, contentId, title) {
    const details = `Content added: ${title} (${contentType})`;
    return await this.log('content_management', 'content_added', details, { contentType, contentId, title });
  }

  async logContentUpdate(contentType, contentId, title, changes) {
    const details = `Content updated: ${title} (${contentType})`;
    return await this.log('content_management', 'content_updated', details, { contentType, contentId, title, changes });
  }

  async logContentDelete(contentType, contentId, title) {
    const details = `Content deleted: ${title} (${contentType})`;
    return await this.log('content_management', 'content_deleted', details, { contentType, contentId, title });
  }

  // System Activities
  async logDatabaseOperation(operation, collection, documentId = null, success = true) {
    const details = `Database ${operation} on ${collection}${documentId ? `/${documentId}` : ''}: ${success ? 'success' : 'failed'}`;
    return await this.log('system_activity', 'database_operation', details, { operation, collection, documentId, success });
  }

  async logFileUpload(fileName, fileSize, fileType, success = true) {
    const details = `File upload ${success ? 'successful' : 'failed'}: ${fileName} (${fileSize} bytes, ${fileType})`;
    return await this.log('system_activity', 'file_upload', details, { fileName, fileSize, fileType, success });
  }

  async logAPICall(endpoint, method, statusCode, responseTime) {
    const details = `API call: ${method} ${endpoint} → ${statusCode} (${responseTime}ms)`;
    return await this.log('system_activity', 'api_call', details, { endpoint, method, statusCode, responseTime });
  }

  // Error Logging
  async logError(error, context, additionalInfo = {}) {
    const details = `Error in ${context}: ${error.message}`;
    const metadata = {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack,
      context,
      ...additionalInfo
    };
    
    return await this.log('error', 'application_error', details, metadata);
  }

  // Performance Logging
  async logPerformance(metric, value, context) {
    const details = `Performance metric ${metric}: ${value}ms in ${context}`;
    return await this.log('performance', 'performance_metric', details, { metric, value, context });
  }

  async logPageLoad(pageName, loadTime) {
    const details = `Page loaded: ${pageName} (${loadTime}ms)`;
    return await this.log('performance', 'page_load', details, { pageName, loadTime });
  }

  // Lead and Job Application Activities
  async logLeadGeneration(leadData) {
    const details = `Lead generated: ${leadData.email} from ${leadData.company}`;
    return await this.log('lead_management', 'lead_generated', details, { leadData });
  }

  async logJobApplication(jobData) {
    const details = `Job application submitted: ${jobData.email} for ${jobData.role}`;
    return await this.log('job_management', 'job_application_submitted', details, { jobData });
  }
}

// Create singleton instance
const activityLogger = new ActivityLogger();

// Export both the class and the singleton instance
export { ActivityLogger };
export default activityLogger;

// Convenience functions for common activities
export const logActivity = (type, action, details, metadata = {}) => 
  activityLogger.log(type, action, details, metadata);

export const logUserLogin = (email, success = true, errorMessage = null) => 
  activityLogger.logLogin(email, success, errorMessage);

export const logUserLogout = (email) => 
  activityLogger.logLogout(email);

export const logUserRegistration = (email, success = true, errorMessage = null) => 
  activityLogger.logRegistration(email, success, errorMessage);

export const logAdminActivity = (action, details, targetId = null, changes = {}) => 
  activityLogger.logAdminAction(action, details, targetId, changes);

export const logUIChange = (element, oldValue, newValue) => 
  activityLogger.logColorChange(element, oldValue, newValue);

export const logChatActivity = (action, details, metadata = {}) => 
  activityLogger.log('chat_activity', action, details, metadata);

export const logContentActivity = (action, details, metadata = {}) => 
  activityLogger.log('content_management', action, details, metadata);

export const logSystemActivity = (action, details, metadata = {}) => 
  activityLogger.log('system_activity', action, details, metadata);

export const logAppError = (error, context, additionalInfo = {}) => 
  activityLogger.logError(error, context, additionalInfo);

export const logPerformanceMetric = (metric, value, context) => 
  activityLogger.logPerformance(metric, value, context); 